import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, friendlyError } from "@/lib/api";
import { TrustBadge } from "@/components/app/TrustBadge";
import { toast } from "sonner";
import { Send, GitCompareArrows, Sparkles, CheckCircle2, Clock, FileText, Eye, Inbox, CheckCheck, AlertCircle, Globe, ExternalLink, Search, KeyRound, Mail, MailCheck, MailX, MessageSquarePlus, X } from "lucide-react";

// Keep status labels aligned to things we can actually verify.
const RFQ_STATUS_LABEL = {
  draft: { label: "Draft", tone: "slate", desc: "Not yet sent." },
  sent: { label: "Sent", tone: "sky", desc: "Dispatched to selected suppliers." },
  quote_received: { label: "Quote received", tone: "emerald", desc: "At least one supplier has responded." },
  closed: { label: "Closed", tone: "amber", desc: "A quotation has been accepted." },
};

const TONE = {
  slate: "bg-slate-100 text-slate-600 border-slate-200",
  sky: "bg-sky-50 text-sky-700 border-sky-200",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  amber: "bg-amber-50 text-amber-800 border-amber-200",
};

const ACTIVITY_ICON = {
  created: FileText,
  sent: Send,
  viewed: Eye,
  quote_received: Inbox,
  quote_accepted: CheckCheck,
};

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function StatusPill({ status }) {
  const s = RFQ_STATUS_LABEL[status] || RFQ_STATUS_LABEL.draft;
  return <span className={`pill ${TONE[s.tone]}`} data-testid={`rfq-status-${status}`}>{s.label}</span>;
}

export default function RequestDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [req, setReq] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [rfq, setRfq] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [busy, setBusy] = useState("");
  const [loading, setLoading] = useState(true);
  const [discoveryStatus, setDiscoveryStatus] = useState(null);
  const [supplierCountry, setSupplierCountry] = useState("");
  const [emailEvents, setEmailEvents] = useState([]);
  const [logFor, setLogFor] = useState(null); // supplier obj when logging a reply

  const load = async () => {
    try {
      const r = await api.get(`/sourcing/requests/${id}`);
      setReq(r.data);
      const [sup, rfqs, ds] = await Promise.all([
        api.get(`/sourcing/requests/${id}/matched-suppliers`),
        api.get("/rfqs"),
        api.get("/discovery/status").catch(() => ({ data: null })),
      ]);
      setSuppliers(sup.data);
      setDiscoveryStatus(ds.data);
      const match = rfqs.data.find((x) => x.request_id === id);
      if (match) {
        setRfq(match);
        const [q, ev] = await Promise.all([
          api.get("/quotes", { params: { rfq_id: match.id } }),
          api.get(`/rfqs/${match.id}/email-events`).catch(() => ({ data: [] })),
        ]);
        setQuotes(q.data);
        setEmailEvents(ev.data || []);
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const genRfq = async () => {
    setBusy("rfq");
    try {
      const { data } = await api.post(`/sourcing/requests/${id}/rfq`);
      setRfq(data);
      toast.success("RFQ generated. Review before sending.");
    } catch (e) { toast.error(friendlyError(e)); } finally { setBusy(""); }
  };

  const toggle = (sid) => {
    setSelected((s) => { const n = new Set(s); n.has(sid) ? n.delete(sid) : n.add(sid); return n; });
  };

  const sendRfq = async () => {
    if (!rfq || selected.size === 0) { toast.error("Select at least one supplier."); return; }
    setBusy("send");
    try {
      const { data } = await api.post(`/rfqs/${rfq.id}/send`, { supplier_ids: Array.from(selected) });
      setRfq(data);
      const [q, ev] = await Promise.all([
        api.get("/quotes", { params: { rfq_id: rfq.id } }),
        api.get(`/rfqs/${rfq.id}/email-events`).catch(() => ({ data: [] })),
      ]);
      setQuotes(q.data);
      setEmailEvents(ev.data || []);
      const sent = (ev.data || []).filter((e) => e.status === "sent").length;
      const failed = (ev.data || []).filter((e) => e.status === "failed").length;
      if (failed > 0) toast.warning(`RFQ dispatched. ${sent} sent, ${failed} failed.`);
      else toast.success(`RFQ dispatched to ${sent} supplier(s).`);
      setSelected(new Set());
    } catch (e) { toast.error(friendlyError(e)); } finally { setBusy(""); }
  };

  const analyze = async () => {
    if (!rfq) return;
    setBusy("analyze");
    try {
      const { data } = await api.post("/quotes/analyze", { rfq_id: rfq.id });
      setAnalysis(data.analysis);
      toast.success("AI analysis complete");
    } catch (e) { toast.error(friendlyError(e)); } finally { setBusy(""); }
  };

  const accept = async (qid) => {
    if (!window.confirm("Accept this quotation? Nothing is paid or committed externally — human approval remains required for any payment.")) return;
    try {
      await api.post(`/quotes/${qid}/accept`);
      toast.success("Quotation accepted. Order drafted pending approval.");
      nav("/buyer/orders");
    } catch (e) { toast.error(friendlyError(e)); }
  };

  const runDiscovery = async () => {
    if (busy === "discover") return;
    setBusy("discover");
    try {
      const { data } = await api.post(`/sourcing/requests/${id}/discover`, {
        supplier_country: supplierCountry || undefined,
      });
      setSuppliers(data.suppliers);
      if (data.suppliers.length === 0) {
        toast.message("No candidates found", { description: "Try broadening the specifications or removing the country filter." });
      } else {
        toast.success(`${data.suppliers.length} live supplier${data.suppliers.length === 1 ? "" : "s"} discovered`);
      }
    } catch (e) {
      toast.error(friendlyError(e));
    } finally { setBusy(""); }
  };

  if (loading) return <div className="space-y-3"><div className="skeleton h-24"/><div className="skeleton h-56"/></div>;
  if (!req) return <div className="text-sm text-slate-500">Request not found.</div>;

  const sentCount = rfq?.sent_to_supplier_ids?.length || 0;
  const viewedCount = rfq?.viewed_by_supplier_ids?.length || 0;
  const respondedCount = rfq?.responded_by_supplier_ids?.length || 0;

  return (
    <div className="space-y-8">
      <div>
        <div className="eyebrow mb-2">Sourcing request</div>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <h1 className="page-title">{req.requirements?.product_or_service || "Untitled request"}</h1>
          {rfq && <StatusPill status={rfq.status}/>}
        </div>
        <p className="text-sm text-slate-500 mt-2 max-w-3xl" data-selectable>{req.original_text}</p>
        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-slate-500 font-mono" data-selectable>
          <span>ID {req.id.slice(0,8)}</span>
          <span>Created {new Date(req.created_at).toLocaleDateString(undefined, {year:"numeric", month:"short", day:"numeric"})}</span>
          {req.requirements?.destination && <span>→ {req.requirements.destination}</span>}
          {req.requirements?.budget && <span>Budget {req.requirements.currency || ""} {req.requirements.budget}</span>}
        </div>
      </div>

      {/* Requirements */}
      <div className="surface p-5 md:p-6">
        <div className="section-title mb-4">Approved requirements</div>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3 text-sm" data-selectable>
          {Object.entries(req.requirements || {}).filter(([k,v]) => v && k !== "missing_information").map(([k,v]) => (
            <div key={k}>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">{k.replace(/_/g,' ')}</div>
              <div className="text-slate-900">{String(v)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* RFQ Section */}
      <div className="surface p-5 md:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <div className="section-title">Request for Quotation</div>
            {rfq && <div className="text-xs text-slate-500 mt-1">Status: <StatusPill status={rfq.status}/> · <span className="font-mono">{rfq.id.slice(0,8)}</span></div>}
          </div>
          {!rfq && (
            <button onClick={genRfq} disabled={busy === "rfq"} className="btn-gold" data-testid="generate-rfq-btn">
              {busy === "rfq" ? "Generating…" : <>Generate RFQ <Sparkles size={14}/></>}
            </button>
          )}
        </div>

        {rfq ? (
          <>
            {/* Communication counters — only truthfully verifiable states */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <StatCell label="Dispatched" value={sentCount} Icon={Send} testId="sent-count"/>
              <StatCell label="Opened by supplier" value={viewedCount} Icon={Eye} testId="viewed-count" muted={sentCount===0}/>
              <StatCell label="Responded" value={respondedCount} Icon={Inbox} testId="responded-count" muted={sentCount===0}/>
              <StatCell label="Quotes received" value={quotes.length} Icon={CheckCheck} testId="quotes-count"/>
            </div>

            <div className="space-y-4 text-sm">
              <div className="h3">{rfq.content?.title}</div>
              {Object.entries(rfq.content || {}).filter(([k]) => k !== "title").map(([k, v]) => (
                <div key={k}>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">{k.replace(/_/g,' ')}</div>
                  <div className="text-slate-700 whitespace-pre-wrap">{typeof v === "string" ? v : JSON.stringify(v, null, 2)}</div>
                </div>
              ))}
            </div>

            {/* Activity timeline */}
            {rfq.activity && rfq.activity.length > 0 && (
              <div className="mt-7" data-testid="rfq-activity">
                <div className="section-title mb-3">Activity</div>
                <ol className="relative border-l border-slate-200 ml-2 space-y-4">
                  {[...rfq.activity].reverse().map((a, idx) => {
                    const Icon = ACTIVITY_ICON[a.type] || Clock;
                    return (
                      <li key={idx} className="pl-5">
                        <span className="absolute -left-[9px] mt-0.5 h-4 w-4 rounded-full bg-white border border-slate-300 flex items-center justify-center">
                          <Icon size={10} className="text-slate-500"/>
                        </span>
                        <div className="text-sm text-slate-700">{a.note}</div>
                        <div className="text-[11px] text-slate-400 mt-0.5">
                          {new Date(a.at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })} · {timeAgo(a.at)}
                        </div>
                      </li>
                    );
                  })}
                </ol>
                <div className="mt-4 text-[11px] text-slate-400 inline-flex items-start gap-1.5 max-w-xl">
                  <AlertCircle size={12} className="mt-0.5 shrink-0"/>
                  <span>We only record states that the platform can verify directly. &ldquo;Delivered&rdquo; / &ldquo;read&rdquo; receipts from external email are not shown until a real email integration is enabled.</span>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-sm text-slate-500">Generate a professional RFQ from your approved requirements. You review it before anything is sent.</div>
        )}
      </div>

      {/* Suppliers */}
      <div className="surface p-5 md:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="section-title">
                {req.mode === "demo" ? "Matched suppliers (demo)" : "Live supplier discovery"}
              </div>
              {req.mode === "demo"
                ? <span className="pill bg-amber-50 text-amber-800 border-amber-200" data-testid="mode-badge-demo">DEMO · SAMPLE DATA</span>
                : <span className="pill bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="mode-badge-live">LIVE INTERNET RESULTS</span>}
            </div>
            <div className="text-xs text-slate-500 mt-1 max-w-2xl">
              {req.mode === "demo"
                ? "Suppliers below are from the clearly-labeled demo directory — not real quotations."
                : "Results come from a real Internet search. We never fabricate prices, MOQs, certifications or verification."}
            </div>
          </div>
          {rfq && (
            <button onClick={sendRfq} disabled={!rfq || selected.size === 0 || busy === "send"} className="btn-gold" data-testid="send-rfq-btn">
              {busy === "send" ? "Sending…" : <>Send RFQ to {selected.size} supplier(s) <Send size={14}/></>}
            </button>
          )}
        </div>

        {req.mode !== "demo" && (
          <div className="mb-5 rounded-md border border-slate-200 bg-slate-50 p-4" data-testid="discovery-panel">
            {discoveryStatus && !discoveryStatus.available ? (
              <div className="flex items-start gap-3">
                <KeyRound size={16} className="text-amber-600 mt-0.5"/>
                <div className="text-sm">
                  <div className="font-semibold">Live supplier discovery requires a provider API key.</div>
                  <div className="text-slate-600 mt-1">
                    Backend admin: set <span className="font-mono text-xs" data-selectable>BRAVE_SEARCH_API_KEY</span> in <span className="font-mono text-xs" data-selectable>/app/backend/.env</span> and restart. We never ship this key to the browser.
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[220px]">
                  <label htmlFor="supplier-country" className="text-xs text-slate-500 uppercase tracking-wider">Target supplier country (optional)</label>
                  <input
                    id="supplier-country"
                    value={supplierCountry}
                    onChange={(e) => setSupplierCountry(e.target.value)}
                    placeholder="e.g. China, Germany, Vietnam"
                    data-testid="supplier-country-input"
                    className="mt-1 w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
                  />
                </div>
                <button onClick={runDiscovery} disabled={busy === "discover"} className="btn-gold" data-testid="run-discovery-btn">
                  {busy === "discover" ? "Searching the Internet…" : <>Discover live suppliers <Search size={14}/></>}
                </button>
                <div className="text-[11px] text-slate-500 w-full">
                  Uses the configured search provider ({discoveryStatus?.provider || "brave"}) with up to {discoveryStatus?.max_queries || 3} queries. Results are cached for {discoveryStatus?.cache_ttl_hours || 24}h.
                </div>
              </div>
            )}
          </div>
        )}

        {suppliers.length === 0 ? (
          <div className="p-6 text-sm text-slate-500 text-center border border-dashed border-slate-200 rounded-md" data-testid="suppliers-empty">
            {req.mode === "demo" ? "No demo suppliers matched." : "No suppliers discovered yet. Run live discovery to search real Internet sources."}
          </div>
        ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {suppliers.map((s) => {
            const isLive = s.discovery_mode === "live";
            const isSel = selected.has(s.id);
            const sent = rfq?.sent_to_supplier_ids?.includes(s.id);
            const viewed = rfq?.viewed_by_supplier_ids?.includes(s.id);
            const responded = rfq?.responded_by_supplier_ids?.includes(s.id);
            const quote = quotes.find((q) => q.supplier_id === s.id);
            const emailEvent = emailEvents.find((e) => e.supplier_id === s.id);
            const primaryContact = (s.contacts && s.contacts[0]) || null;
            let recipientStatus = null;
            let nextAction = null;
            if (sent) {
              if (responded) { recipientStatus = { key: "quote_received", label: "Quote received", tone: "emerald" }; nextAction = "Review quotation"; }
              else if (viewed) { recipientStatus = { key: "opened", label: "Opened", tone: "sky" }; nextAction = "Awaiting response"; }
              else if (isLive && emailEvent) {
                if (emailEvent.status === "sent") { recipientStatus = { key: "sent", label: `Sent · ${emailEvent.to_email || "email"}`, tone: "sky" }; nextAction = "Awaiting reply"; }
                else { recipientStatus = { key: "failed", label: "Delivery failed", tone: "rose" }; nextAction = "Check error"; }
              }
              else { recipientStatus = { key: "sent", label: "Ready to send — email integration required", tone: "slate" }; nextAction = "Enable email dispatch"; }
            }
            const location = [s.country || s.country_hint, s.city].filter(Boolean).join(", ");
            return (
              <div key={s.id} className={`rounded-md border p-4 overflow-hidden min-w-0 transition-colors ${isSel ? "border-[hsl(var(--gold))] bg-[hsl(var(--gold))]/5" : "border-slate-200 bg-white hover:border-slate-300"}`} data-testid={`supplier-card-${s.id}`}>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-[15px] tracking-tight truncate" data-selectable>{s.company_name || s.source_domain}</div>
                    <div className="text-xs text-slate-500 mt-0.5 truncate" data-selectable>
                      {isLive ? (location || "Location not publicly available") : `${s.country} · ${s.years_in_business || "—"} yrs`}
                    </div>
                  </div>
                  {isLive ? (
                    <span className="pill bg-emerald-50 text-emerald-700 border-emerald-200" title="Source found via web search — not independently verified">Source found</span>
                  ) : (
                    <TrustBadge level={s.trust_level}/>
                  )}
                </div>

                {isLive ? (
                  <>
                    {s.source_description && (
                      <div className="text-xs text-slate-600 mt-3 line-clamp-3 min-h-[48px]" data-selectable>{s.source_description}</div>
                    )}
                    {/* Real contact emails — only shown when the supplier's own site lists them. */}
                    {primaryContact ? (
                      <div className="mt-3 rounded-md border border-emerald-100 bg-emerald-50/50 p-2.5" data-testid={`contact-${s.id}`}>
                        <div className="flex items-start gap-2">
                          <Mail size={14} className="text-emerald-700 mt-0.5 shrink-0"/>
                          <div className="min-w-0 flex-1">
                            <div className="text-[12px] font-mono text-emerald-900 truncate" data-selectable>{primaryContact.email}</div>
                            <a href={primaryContact.found_at_url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-emerald-700 hover:underline inline-flex items-center gap-1 mt-0.5" data-testid={`contact-source-${s.id}`}>
                              Verify at source <ExternalLink size={10}/>
                            </a>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3 text-[11px] text-slate-500" data-testid={`contact-none-${s.id}`}>
                        <span className="text-slate-400">Email:</span> Not publicly listed on their website
                      </div>
                    )}
                    <div className="mt-2 text-[11px] text-slate-500 space-y-0.5" data-selectable>
                      <div><span className="text-slate-400">MOQ:</span> Not publicly available</div>
                      <div><span className="text-slate-400">Certifications:</span> Not publicly available</div>
                      {s.is_marketplace_listing && <div className="text-amber-600">Marketplace listing — verify the actual supplier at the source.</div>}
                    </div>
                    <div className="mt-3 text-[11px] text-slate-500">
                      <span className="text-slate-400">Why it matches:</span> {s.match_reasons?.[0] || `Matched: ${s.matched_query}`}
                    </div>
                    {emailEvent?.status === "failed" && emailEvent.error && (
                      <div className="mt-2 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5" data-testid={`send-error-${s.id}`}>
                        <span className="font-medium">Delivery failed:</span> {emailEvent.error}
                      </div>
                    )}
                    <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                      <a href={s.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-[hsl(var(--navy))] hover:underline" data-testid={`view-source-${s.id}`}>
                        <ExternalLink size={12}/> View source
                      </a>
                      {recipientStatus ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`pill ${
                            recipientStatus.tone === "slate" ? "bg-slate-100 text-slate-600 border-slate-200" :
                            recipientStatus.tone === "sky" ? "bg-sky-50 text-sky-700 border-sky-200" :
                            recipientStatus.tone === "rose" ? "bg-rose-50 text-rose-700 border-rose-200" :
                            "bg-emerald-50 text-emerald-700 border-emerald-200"
                          }`} data-testid={`rfq-status-${recipientStatus.key}-${s.id}`}>
                            {recipientStatus.key === "sent" && <MailCheck size={10} className="inline mr-1 mb-0.5"/>}
                            {recipientStatus.key === "failed" && <MailX size={10} className="inline mr-1 mb-0.5"/>}
                            {recipientStatus.label}
                          </span>
                          {sent && !responded && (
                            <button
                              type="button"
                              onClick={() => setLogFor(s)}
                              className="inline-flex items-center gap-1 text-[11px] font-medium text-[hsl(var(--navy))] hover:underline"
                              data-testid={`log-reply-${s.id}`}
                            >
                              <MessageSquarePlus size={12}/> Log supplier reply
                            </button>
                          )}
                        </div>
                      ) : (
                        <button
                          disabled={!rfq || !primaryContact}
                          onClick={() => toggle(s.id)}
                          className={`text-xs font-medium ${isSel ? "text-[hsl(var(--gold))]" : "text-[hsl(var(--navy))]"} disabled:opacity-50`}
                          title={!primaryContact ? "No publicly listed email — cannot select for RFQ email dispatch" : ""}
                          data-testid={`select-supplier-${s.id}`}
                        >
                          {isSel ? "Selected" : primaryContact ? "Select for RFQ" : "No email available"}
                        </button>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-xs text-slate-600 mt-3 line-clamp-3 min-h-[48px]" data-selectable>{s.description}</div>
                    <div className="flex flex-wrap gap-1 mt-3">
                      {(s.categories || []).slice(0,3).map(c => <span key={c} className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{c}</span>)}
                    </div>
                    <div className="mt-3 text-[11px] text-slate-500 truncate" data-selectable>MOQ {s.min_order_qty || "—"} · {s.lead_time_days || "—"}d</div>
                    {recipientStatus ? (
                      <div className="mt-3 pt-3 border-t border-slate-100 space-y-1.5">
                        <div className="flex items-center justify-between gap-2 text-xs"><span className="text-slate-500">Channel</span><span className="font-medium text-slate-700">In-platform (demo)</span></div>
                        <div className="flex items-center justify-between gap-2 text-xs"><span className="text-slate-500">Status</span><span className={`pill ${recipientStatus.tone === "slate" ? "bg-slate-100 text-slate-600 border-slate-200" : recipientStatus.tone === "sky" ? "bg-sky-50 text-sky-700 border-sky-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`} data-testid={`rfq-status-${recipientStatus.key}`}>{recipientStatus.label}</span></div>
                        {quote && (<div className="flex items-center justify-between gap-2 text-xs pt-1"><span className="text-slate-500">Quote</span><span className="font-mono tabular-nums text-slate-900" data-selectable>{quote.currency} {quote.price?.toLocaleString()}</span></div>)}
                      </div>
                    ) : (
                      <div className="mt-3 flex items-center justify-end">
                        <button disabled={!rfq} onClick={() => toggle(s.id)} className={`text-xs font-medium ${isSel ? "text-[hsl(var(--gold))]" : "text-[hsl(var(--navy))]"} disabled:opacity-50`} data-testid={`select-supplier-${s.id}`}>
                          {isSel ? "Selected" : "Select"}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
        )}

        {rfq && (
          <div className="mt-4 text-[11px] text-slate-500 flex items-start gap-1.5 max-w-2xl">
            <AlertCircle size={12} className="mt-0.5 shrink-0"/>
            <span>Suppliers with a publicly listed email on their own website receive the RFQ by real email. Delivery (open/read) and automatic reply parsing are not available on the managed email path — when a supplier replies to you by email, use &quot;Log supplier reply&quot; to save their quotation here.</span>
          </div>
        )}
      </div>
      {/* Quotes */}
      {quotes.length > 0 && (
        <div className="surface p-5 md:p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="section-title">Quotations received <span className="text-sm text-slate-500 font-normal">({quotes.length})</span></div>
            <button onClick={analyze} disabled={busy==='analyze'} className="btn-gold" data-testid="analyze-quotes-btn">
              {busy === "analyze" ? "Analyzing…" : <>AI comparison <GitCompareArrows size={14}/></>}
            </button>
          </div>
          <div className="space-y-3">
            {quotes.map((q) => (
              <div key={q.id} className="rounded-md border border-slate-200 p-4" data-testid={`quote-row-${q.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold tracking-tight" data-selectable>{q.supplier_name}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="pill bg-emerald-50 text-emerald-700 border-emerald-200">Quote received</span>
                      {q.is_demo && <span className="pill bg-amber-50 text-amber-800 border-amber-200">Demo sample</span>}
                      {q.source === "buyer-logged" && <span className="pill bg-slate-100 text-slate-700 border-slate-200" title="Buyer manually logged this reply from the supplier">Buyer-logged</span>}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-xl tabular-nums text-slate-900" data-selectable>{q.currency} {q.price?.toLocaleString()}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">unit price excludes other costs</div>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-x-5 gap-y-2 text-xs mt-4 pt-3 border-t border-slate-100">
                  <Field label="MOQ" value={q.moq}/>
                  <Field label="Lead time" value={q.lead_time_days ? `${q.lead_time_days} days` : null}/>
                  <Field label="Incoterms" value={q.shipping_terms}/>
                  <Field label="Payment terms" value={q.payment_terms}/>
                  <Field label="Validity" value={q.validity_days ? `${q.validity_days} days` : null}/>
                  <Field label="Warranty" value={q.warranty}/>
                  <Field label="Certifications" value={q.certifications}/>
                  <Field label="Other costs" value={q.other_costs ? `${q.currency} ${q.other_costs.toLocaleString()}` : null}/>
                </div>
                {q.product_specifications && (
                  <div className="mt-3 pt-3 border-t border-slate-100 text-xs" data-selectable>
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Product specifications</div>
                    <div className="text-slate-700 whitespace-pre-wrap">{q.product_specifications}</div>
                  </div>
                )}
                {q.notes && (
                  <div className="mt-3 pt-3 border-t border-slate-100 text-xs" data-selectable>
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Supplier message</div>
                    <div className="text-slate-700 whitespace-pre-wrap">{q.notes}</div>
                  </div>
                )}
                <div className="mt-4 flex items-center justify-end">
                  <button onClick={() => accept(q.id)} className="btn-gold" data-testid={`accept-quote-${q.id}`}>
                    Accept quotation <CheckCheck size={14}/>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Analysis */}
      {analysis && (
        <div className="surface p-5 md:p-6" data-testid="analysis-panel">
          <div className="eyebrow mb-2">AI procurement analysis</div>
          <div className="section-title mb-3">Transparent comparison</div>
          <p className="text-sm text-slate-600 mb-5">{analysis.summary}</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {(analysis.per_supplier || []).map((p) => (
              <div key={p.supplier_id} className="rounded-md border border-slate-200 p-4">
                <div className="flex items-center justify-between">
                  <div className="font-semibold">{p.supplier_name}</div>
                  <div className="text-xs font-mono text-[hsl(var(--gold))]">score {Math.round(p.score)}</div>
                </div>
                <div className="mt-3 text-xs">
                  <div className="text-emerald-700 font-medium">Strengths</div>
                  <ul className="list-disc list-inside text-slate-600">{(p.strengths||[]).map((x,i)=>(<li key={i}>{x}</li>))}</ul>
                  <div className="text-rose-700 font-medium mt-2">Weaknesses</div>
                  <ul className="list-disc list-inside text-slate-600">{(p.weaknesses||[]).map((x,i)=>(<li key={i}>{x}</li>))}</ul>
                  {p.hidden_costs && <div className="mt-2 text-slate-700"><span className="font-medium">Hidden costs:</span> {p.hidden_costs}</div>}
                  {p.risk_notes && <div className="mt-1 text-slate-700"><span className="font-medium">Risk:</span> {p.risk_notes}</div>}
                </div>
              </div>
            ))}
          </div>
          {analysis.recommendation && (
            <div className="mt-5 rounded-md bg-[hsl(var(--gold))]/10 border border-[hsl(var(--gold))]/30 p-4" data-testid="recommendation-block">
              <div className="eyebrow mb-1">AI recommendation</div>
              <div className="text-sm"><span className="font-medium">{analysis.recommendation.reasoning}</span></div>
              {analysis.recommendation.caveats && <div className="text-xs text-slate-600 mt-2">Caveats: {analysis.recommendation.caveats}</div>}
              <div className="text-xs text-slate-500 mt-3">You remain the decision-maker. GlobalBuy AI never purchases on your behalf.</div>
            </div>
          )}
        </div>
      )}

      {logFor && rfq && (
        <LogReplyModal
          supplier={logFor}
          rfqId={rfq.id}
          onClose={() => setLogFor(null)}
          onSaved={async () => {
            setLogFor(null);
            const [q, ev, rfqs] = await Promise.all([
              api.get("/quotes", { params: { rfq_id: rfq.id } }),
              api.get(`/rfqs/${rfq.id}/email-events`).catch(() => ({ data: [] })),
              api.get("/rfqs"),
            ]);
            setQuotes(q.data);
            setEmailEvents(ev.data || []);
            const refreshed = (rfqs.data || []).find((x) => x.id === rfq.id);
            if (refreshed) setRfq(refreshed);
            toast.success("Reply logged. Quotation added to this RFQ.");
          }}
        />
      )}
    </div>
  );
}

function LogReplyModal({ supplier, rfqId, onClose, onSaved }) {
  const [form, setForm] = useState({
    price: "",
    currency: "USD",
    moq: "",
    lead_time_days: "",
    shipping_terms: "",
    payment_terms: "",
    validity_days: "",
    certifications: "",
    warranty: "",
    other_costs: "",
    product_specifications: "",
    notes: "",
    reply_source: "email",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    const price = parseFloat(form.price);
    if (!price || price <= 0) { toast.error("Enter a valid price."); return; }
    setSaving(true);
    try {
      await api.post(`/rfqs/${rfqId}/log-reply`, {
        rfq_id: rfqId,
        supplier_id: supplier.id,
        price,
        currency: form.currency || "USD",
        moq: form.moq || null,
        lead_time_days: form.lead_time_days ? parseInt(form.lead_time_days, 10) : null,
        shipping_terms: form.shipping_terms || null,
        payment_terms: form.payment_terms || null,
        validity_days: form.validity_days ? parseInt(form.validity_days, 10) : null,
        certifications: form.certifications || null,
        warranty: form.warranty || null,
        other_costs: form.other_costs ? parseFloat(form.other_costs) : null,
        product_specifications: form.product_specifications || null,
        notes: form.notes || null,
        reply_source: form.reply_source || "email",
      });
      await onSaved();
    } catch (err) { toast.error(friendlyError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="log-reply-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-xl bg-white rounded-t-xl sm:rounded-xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold">Log supplier reply</div>
            <div className="text-xs text-slate-500 truncate" data-selectable>{supplier.company_name || supplier.source_domain}</div>
          </div>
          <button type="button" onClick={onClose} className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-slate-100" aria-label="Close" data-testid="log-reply-close">
            <X size={16}/>
          </button>
        </div>
        <div className="px-5 py-4 space-y-3 text-sm">
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-[12px] text-amber-900 flex items-start gap-2">
            <AlertCircle size={14} className="mt-0.5 shrink-0"/>
            <span>Save the pricing exactly as the supplier replied. We mark this quotation as <span className="font-mono">buyer-logged</span> so it is clearly distinguished from auto-captured replies.</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Lbl label="Price">
              <input required type="number" step="0.01" value={form.price} onChange={set("price")} className="inp" data-testid="log-reply-price"/>
            </Lbl>
            <Lbl label="Currency">
              <input value={form.currency} onChange={set("currency")} className="inp" data-testid="log-reply-currency"/>
            </Lbl>
            <Lbl label="MOQ">
              <input value={form.moq} onChange={set("moq")} className="inp" placeholder="e.g. 500 units" data-testid="log-reply-moq"/>
            </Lbl>
            <Lbl label="Lead time (days)">
              <input type="number" value={form.lead_time_days} onChange={set("lead_time_days")} className="inp" data-testid="log-reply-lead"/>
            </Lbl>
            <Lbl label="Shipping">
              <input value={form.shipping_terms} onChange={set("shipping_terms")} className="inp" placeholder="FOB / CIF / DDP" data-testid="log-reply-shipping"/>
            </Lbl>
            <Lbl label="Payment terms">
              <input value={form.payment_terms} onChange={set("payment_terms")} className="inp" placeholder="T/T 30/70" data-testid="log-reply-payment"/>
            </Lbl>
            <Lbl label="Validity (days)">
              <input type="number" value={form.validity_days} onChange={set("validity_days")} className="inp" data-testid="log-reply-validity"/>
            </Lbl>
            <Lbl label="Warranty">
              <input value={form.warranty} onChange={set("warranty")} className="inp" data-testid="log-reply-warranty"/>
            </Lbl>
            <Lbl label="Certifications">
              <input value={form.certifications} onChange={set("certifications")} className="inp" placeholder="ISO 9001, CE" data-testid="log-reply-certs"/>
            </Lbl>
            <Lbl label="Other costs">
              <input type="number" step="0.01" value={form.other_costs} onChange={set("other_costs")} className="inp" data-testid="log-reply-other"/>
            </Lbl>
          </div>
          <Lbl label="Product specifications (from supplier reply)">
            <textarea value={form.product_specifications} onChange={set("product_specifications")} rows={3} className="inp" data-testid="log-reply-specs"/>
          </Lbl>
          <Lbl label="Supplier notes">
            <textarea value={form.notes} onChange={set("notes")} rows={2} className="inp" data-testid="log-reply-notes"/>
          </Lbl>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-5 py-3 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="h-9 px-3 rounded-md border border-slate-300 text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
          <button type="submit" disabled={saving} className="btn-gold" data-testid="log-reply-submit">
            {saving ? "Saving…" : "Save quotation"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Lbl({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">{label}</span>
      {children}
    </label>
  );
}

function StatCell({ label, value, Icon, testId, muted }) {
  return (
    <div className={`rounded-md border p-3 ${muted ? "border-slate-200 bg-slate-50" : "border-slate-200 bg-white"}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
        <Icon size={14} className="text-slate-400"/>
      </div>
      <div className="text-xl font-semibold tracking-tight mt-1 tabular-nums">{value}</div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">{label}</div>
      <div className="text-slate-800" data-selectable>{value || <span className="text-slate-400">—</span>}</div>
    </div>
  );
}
